// Role-based Access Control (RBAC) Configuration
export type UserRole = 'admin' | 'manager' | 'exclusive' | 'USER' | 'DEALER'; // Admin | Manager | Exclusive | User | Dealer

// Route permissions mapping
export const rolePermissions: Record<UserRole, string[]> = {
    admin: [
        'dashboard',
        'create-project',
        'projects',
        'projects/view',
        'update-project',
        'investments',
        'users',
        'user-details',
        'categories',
        'notifications',
        'profile',
        'admin-users',
        'bank-accounts',
        'payment-methods',
        'wallet',
        'cta-management',
        'blogs',
        'testimonials',
        'testimonials/create',
        'testimonials/edit',
        'child-sponsorship',
        'child-sponsorship/create',
        'child-sponsorship/edit',
        'child-sponsorship/updates',
        'contact-submissions',
        'contact-submissions/:id',
        'solar-leads',
        'membership-management',
        'apartments',
        'bookings',
        'bookings/:id',
        'memberships',
        'marketplace-listings',
        'marketplace-listings/create',
        'marketplace-listings/edit',
        'marketplace-inquiries',
        'marketplace-transactions',
        'marketplace-settings',
        'marketplace-kyc-requests',
        'dealer-management',
        'sold-listings',
        'pending-listings',
        'jobs'
    ],
    manager: [
        'dashboard',
        'create-project',
        'projects',
        'projects/view',
        'update-project',
        'investments',
        'users',
        'user-details',
        'categories',
        'notifications',
        'profile',
        'cta-management',
        'blogs',
        'testimonials',
        'testimonials/create',
        'testimonials/edit',
        'child-sponsorship',
        'child-sponsorship/create',
        'child-sponsorship/edit',
        'child-sponsorship/updates',
        'contact-submissions',
        'contact-submissions/:id',
        'solar-leads',
        'membership-management',
        'apartments',
        'bookings',
        'bookings/:id',
        'memberships',
        'marketplace-listings',
        'marketplace-listings/create',
        'marketplace-listings/edit',
        'marketplace-inquiries',
        'marketplace-transactions',
        'marketplace-settings',
        'marketplace-kyc-requests',
        'dealer-management',
        'sold-listings',
        'pending-listings',
        'jobs'
    ],
    exclusive: [
        'dashboard',
        'projects',
        'projects/view',
        'investments',
        'users',
        'user-details',
        'marketplace-listings',
        'marketplace-inquiries',
        'marketplace-transactions',
        'marketplace-kyc-requests',
        'dealer-management',
        'sold-listings',
        'pending-listings',
        'contact-submissions',
        'contact-submissions/:id',
        'profile'
    ],
    USER: [],
    DEALER: []
};

// Navigation items configuration
export interface NavigationItem {
    to: string;
    icon: any;
    label: string;
    description: string;
    group: string;
    roles: UserRole[];
}

export const navigationItems: NavigationItem[] = [
    // ==========================================
    // 1. OVERVIEW
    // ==========================================
    {
        to: '/admin',
        icon: 'Home',
        label: 'Dashboard',
        description: 'Overview',
        group: 'Overview',
        roles: ['admin', 'manager', 'exclusive']
    },

    // ==========================================
    // 2. CORE MANAGEMENT
    // ==========================================
    {
        to: '/admin/users',
        icon: 'Users',
        label: 'Users',
        description: 'Manage users',
        group: 'User Control',
        roles: ['admin', 'manager', 'exclusive']
    },
    {
        to: '/admin/dealer-management',
        icon: 'Store',
        label: 'Dealers',
        description: 'Manage dealerships',
        group: 'User Control',
        roles: ['admin', 'manager', 'exclusive']
    },

    // ==========================================
    // 3. INVESTMENT HUB
    // ==========================================
    {
        to: '/admin/projects',
        icon: 'Building2',
        label: 'Projects',
        description: 'All projects',
        group: 'Investments',
        roles: ['admin', 'manager', 'exclusive']
    },
    {
        to: '/admin/investments',
        icon: 'TrendingUp',
        label: 'Investments',
        description: 'History',
        group: 'Investments',
        roles: ['admin', 'manager', 'exclusive']
    },
    {
        to: '/admin/categories',
        icon: 'Briefcase',
        label: 'Categories',
        description: 'Manage types',
        group: 'Categories',
        roles: ['admin', 'manager']
    },

    // ==========================================
    // 4. MARKETPLACE (AUTO)
    // ==========================================
    {
        to: '/admin/marketplace-listings',
        icon: 'ShoppingBag',
        label: 'Listings',
        description: 'Marketplace listings',
        group: 'Marketplace',
        roles: ['admin', 'manager', 'exclusive']
    },
    {
        to: '/admin/pending-listings',
        icon: 'Clock',
        label: 'Pending Listings',
        description: 'Needs approval',
        group: 'Marketplace',
        roles: ['admin', 'manager', 'exclusive']
    },
    {
        to: '/admin/sold-listings',
        icon: 'CheckCircle',
        label: 'Sold Listings',
        description: 'Past sales',
        group: 'Marketplace',
        roles: ['admin', 'manager', 'exclusive']
    },
    {
        to: '/admin/marketplace-kyc-requests',
        icon: 'Shield',
        label: 'Verify Dealers',
        description: 'Check IDs',
        group: 'Verify Dealers',
        roles: ['admin', 'manager', 'exclusive']
    },
    {
        to: '/admin/marketplace-inquiries',
        icon: 'Mail',
        label: 'Messages',
        description: 'Contact requests',
        group: 'Messages',
        roles: ['admin', 'manager', 'exclusive']
    },
    {
        to: '/admin/marketplace-transactions',
        icon: 'CreditCard',
        label: 'Payments',
        description: 'Transactions',
        group: 'Payments',
        roles: ['admin', 'manager', 'exclusive']
    },
    {
        to: '/admin/marketplace-settings',
        icon: 'Settings',
        label: 'Settings',
        description: 'App settings',
        group: 'Marketplace Settings',
        roles: ['admin', 'manager']
    },

    // ==========================================
    // 5. FINANCIAL CONTROL
    // ==========================================
    {
        to: '/admin/bank-accounts',
        icon: 'CreditCard',
        label: 'Bank Accounts',
        description: 'Banks',
        group: 'Financials',
        roles: ['admin']
    },
    {
        to: '/admin/wallet',
        icon: 'Wallet',
        label: 'Wallet',
        description: 'Balances',
        group: 'Financials',
        roles: ['admin']
    },
    {
        to: '/admin/payment-methods',
        icon: 'ShoppingBag',
        label: 'Payment Methods',
        description: 'Payment types',
        group: 'Financials',
        roles: ['admin']
    },

    // ==========================================
    // 6. SPECIAL SERVICES
    // ==========================================
    {
        to: '/admin/child-sponsorship',
        icon: 'Heart',
        label: 'Child Sponsorship',
        description: 'Manage kids',
        group: 'Child Sponsorship',
        roles: ['admin', 'manager']
    },
    {
        to: '/admin/apartments',
        icon: 'Building2',
        label: 'Apartments',
        description: 'Manage flats',
        group: 'Apartments & Bookings',
        roles: ['admin', 'manager']
    },
    {
        to: '/admin/bookings',
        icon: 'Calendar',
        label: 'Bookings',
        description: 'Renting',
        group: 'Apartments & Bookings',
        roles: ['admin', 'manager']
    },
    {
        to: '/admin/membership-management',
        icon: 'Star',
        label: 'Membership Plans',
        description: 'Tiers',
        group: 'Memberships',
        roles: ['admin', 'manager']
    },
    {
        to: '/admin/memberships',
        icon: 'User',
        label: 'Subscriptions',
        description: 'Paying users',
        group: 'Memberships',
        roles: ['admin', 'manager']
    },
    {
        to: '/admin/solar-leads',
        icon: 'Mail',
        label: 'Solar Leads',
        description: 'Solar requests',
        group: 'Solar Leads',
        roles: ['admin', 'manager']
    },

    // ==========================================
    // 7. CONTENT & ENGAGEMENT
    // ==========================================
    {
        to: '/admin/blogs',
        icon: 'MessageSquare',
        label: 'Blog',
        description: 'Posts',
        group: 'Blog & Testimonials',
        roles: ['admin', 'manager']
    },
    {
        to: '/admin/testimonials',
        icon: 'CheckCircle',
        label: 'Testimonials',
        description: 'Reviews',
        group: 'Blog & Testimonials',
        roles: ['admin', 'manager']
    },
    {
        to: '/admin/cta-management',
        icon: 'Target',
        label: 'App Ads Slider',
        description: 'Manage app ads slider',
        group: 'App Ads Slider',
        roles: ['admin', 'manager']
    },
    {
        to: '/admin/jobs',
        icon: 'Briefcase',
        label: 'Jobs',
        description: 'Hiring',
        group: 'Jobs',
        roles: ['admin', 'manager']
    },

    // ==========================================
    // 8. COMMUNICATIONS
    // ==========================================
    {
        to: '/admin/contact-submissions',
        icon: 'Mail',
        label: 'Contact Us',
        description: 'User messages',
        group: 'Contact Us',
        roles: ['admin', 'manager', 'exclusive']
    },
    {
        to: '/admin/notifications',
        icon: 'Bell',
        label: 'Notifications',
        description: 'Send alerts',
        group: 'Notifications',
        roles: ['admin', 'manager']
    },

    // ==========================================
    // 9. ROLE MANAGEMENT
    // ==========================================
    {
        to: '/admin/profile',
        icon: 'User',
        label: 'Profile',
        description: 'Settings',
        group: 'Role Management',
        roles: ['admin', 'manager', 'exclusive']
    },
    {
        to: '/admin/admin-users',
        icon: 'Shield',
        label: 'Roles & Permission',
        description: 'Manage admin roles and permissions',
        group: 'Role Management',
        roles: ['admin']
    },
];

// Utility functions
export const hasPermission = (userRole: UserRole, route: string): boolean => {
    // Admin has access to everything
    if (userRole === 'admin') return true;
    const permissions = rolePermissions[userRole] || [];
    return permissions.includes(route);
};

export const getNavigationForRole = (userRole: UserRole): NavigationItem[] => {
    // Admin sees all navigation items
    if (userRole === 'admin') return navigationItems;
    return navigationItems.filter(item => item.roles.includes(userRole));
};

export const groupNavigationByCategory = (items: NavigationItem[]) => {
    const groups: Record<string, NavigationItem[]> = {};

    items.forEach(item => {
        if (!groups[item.group]) {
            groups[item.group] = [];
        }
        groups[item.group].push(item);
    });

    return groups;
};

// Route protection helper
export const isRouteAccessible = (userRole: UserRole, pathname: string): boolean => {
    console.log('🔐 isRouteAccessible called with:', { userRole, pathname });

    // Admin has access to everything
    if (userRole === 'admin') {
        console.log('✅ Admin access granted for:', pathname);
        return true;
    }

    // Handle root dashboard routes
    if (pathname === '/admin' || pathname === '/dashboard') {
        console.log('✅ Root dashboard route - allowing access');
        return true;
    }

    // Extract route from pathname
    let route = pathname.replace('/admin/', '').replace('/dashboard/', '');
    // Remove trailing slash if present
    route = route.replace(/\/$/, '');
    console.log('📍 Extracted route:', route);

    // If route is empty, allow access (root admin route)
    if (!route) {
        console.log('✅ Empty route (root) - allowing access');
        return true;
    }

    // Handle dynamic routes for child sponsorship
    if (route.startsWith('child-sponsorship/')) {
        const baseRoute = 'child-sponsorship';
        return hasPermission(userRole, baseRoute);
    }

    // Handle apartment dynamic routes (view and edit)
    if (route.startsWith('apartments/')) {
        const baseRoute = 'apartments';
        return hasPermission(userRole, baseRoute);
    }

    // Handle booking dynamic routes (view details)
    if (route.startsWith('bookings/')) {
        const baseRoute = 'bookings';
        return hasPermission(userRole, baseRoute);
    }

    // Handle solar leads dynamic routes (view details)
    if (route.startsWith('solar-leads/')) {
        const baseRoute = 'solar-leads';
        return hasPermission(userRole, baseRoute);
    }

    // Handle membership dynamic routes (view and edit)
    if (route.startsWith('memberships/')) {
        const baseRoute = 'memberships';
        return hasPermission(userRole, baseRoute);
    }

    // Handle marketplace dynamic routes
    if (route.startsWith('marketplace-listings/')) {
        const baseRoute = 'marketplace-listings';
        return hasPermission(userRole, baseRoute);
    }

    // Handle marketplace inquiries dynamic routes (view)
    if (route.startsWith('marketplace-inquiries/')) {
        const baseRoute = 'marketplace-inquiries';
        return hasPermission(userRole, baseRoute);
    }

    // Handle marketplace transactions dynamic routes (view)
    if (route.startsWith('marketplace-transactions/')) {
        const baseRoute = 'marketplace-transactions';
        return hasPermission(userRole, baseRoute);
    }

    // Handle marketplace KYC requests dynamic routes (view)
    if (route.startsWith('marketplace-kyc-requests/')) {
        const baseRoute = 'marketplace-kyc-requests';
        return hasPermission(userRole, baseRoute);
    }

    // Handle dealer management dynamic routes (view details)
    if (route.startsWith('dealer-management/')) {
        const baseRoute = 'dealer-management';
        return hasPermission(userRole, baseRoute);
    }

    // Handle projects view route (view details)
    if (route === 'projects/view' || route.startsWith('projects/view')) {
        const baseRoute = 'projects';
        return hasPermission(userRole, baseRoute);
    }

    // Handle other dynamic routes
    if (route.startsWith('user-details/') || route.startsWith('cta-management/') || route.startsWith('blogs/') || route.startsWith('testimonials/') || route.startsWith('contact-submissions/') || route.startsWith('membership-management/') || route.startsWith('jobs/')) {
        const baseRoute = route.split('/')[0];
        return hasPermission(userRole, baseRoute);
    }

    // Handle update-project route
    if (route === 'update-project') {
        return userRole === 'admin' || userRole === 'manager';
    }

    const hasPerm = hasPermission(userRole, route);
    console.log('🔍 Final permission check:', { route, userRole, hasPerm });
    return hasPerm;
};